import { z } from 'zod';
import {
  optionalAddress,
  requiredAddress,
  requiredPastDate,
  requiredPhoneNumber,
  requiredState,
  requiredString,
  requiredZipCode,
  zt,
} from '@/util/validators';
import { useLoggedUser } from '@/contexts/current-user-context';
import { getGetPatientSurrogateQueryKey } from '@/api/get-surrogate-info';
import styles from './create-profile-surrogate-page.module.css';
import { getGetPatientQueryKey } from '@/api/get-personal-info';
import { SurrogateForm } from '@/components/surrogate/surrogate-form';
import { useNavigate } from '@/util/react-router-dom';
import { useUpdatePatientSurrogate } from '@/api/update-surrogate-info';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useTranslation } from '@/contexts/i18n-context';
import { useMediaQuery } from '@/hooks/use-media-query';
import { GradientFormTitle } from '@/components/containers/gradient-form-title';
import { ProfileCreationNavigation } from '@/components/navigation/profile-creation-navigation';
import { PatientRelationships, PreferredTitle } from '@/api/api.schemas';
import { NavButton } from '@/components/navigation/nav-button';
import { PageContainer } from '@/components/containers/page-container';
import { formatPhoneNumber, getAge } from '@/util/helpers';
import { useProfileCreation } from '@/contexts/profile-creation-context';
import {
  getSurrogateCreationConfig,
  getSurrogateEditConfig,
  Mode,
} from '../profile-creation/demographics-config';

export const SurrogateSchema = z.object({
  relationshipToPatient: z.nativeEnum(PatientRelationships, {
    message: zt('common.errors.required'),
  }),
  firstName: requiredString(),
  lastName: requiredString(),
  preferredTitle: z.nativeEnum(PreferredTitle, {
    errorMap: () => ({ message: zt('common.errors.required') }),
  }),
  address1: requiredAddress(),
  address2: optionalAddress().nullish(),
  city: requiredString(),
  state: requiredState(),
  zip: requiredZipCode(),
  email: requiredString().email(zt('surrogate.email.error')),
  phone: requiredPhoneNumber(zt('surrogate.phone.error')),
  birthday: requiredPastDate().refine(
    (val) => {
      const age = getAge(val);
      return age !== null && age >= 18;
    },
    {
      message: zt('surrogate.birthday.error'),
    },
  ),
  textMe: z.boolean(),
  textPatient: z.boolean(),
});

export type FormValues = z.infer<typeof SurrogateSchema>;

interface CreateProfileSurrogatePageProps {
  mode: Mode;
}

export function CreateProfileSurrogatePage({ mode }: CreateProfileSurrogatePageProps) {
  const { user } = useLoggedUser();
  const { mutate: saveSurrogateInfo, isPending } = useUpdatePatientSurrogate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const isMobile = useMediaQuery('--screen-md');
  const { patient } = useProfileCreation();

  if (!patient) {
    return null;
  }

  const config = mode === 'create' ? getSurrogateCreationConfig(t) : getSurrogateEditConfig(t);

  const defaultValues = patient.surrogate
    ? {
        ...patient.surrogate,
        phone: formatPhoneNumber(patient.surrogate.phone),
        textMe: patient.surrogate.textMe ?? false,
        textPatient: patient.textMe ?? false,
      }
    : { textPatient: false, textMe: false };

  const onSubmit = (data: FormValues) => {
    saveSurrogateInfo(
      {
        patientId: user.id,
        data: {
          surrogate: data,
          textPatient: mode === 'create' ? data.textPatient : (patient.textMe ?? false),
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getGetPatientSurrogateQueryKey(user.id),
            refetchType: 'all',
          });

          queryClient.invalidateQueries({
            queryKey: getGetPatientQueryKey(user.id),
            refetchType: 'all',
          });
          navigate(config.nextRoute);
        },
      },
    );
  };

  return (
    <PageContainer>
      {!isMobile && (
        <NavButton text={config.navigationButton.textKey} to={config.navigationButton.route} />
      )}
      <GradientFormTitle>
        {isMobile && (
          <NavButton text={config.navigationButton.textKey} to={config.navigationButton.route} />
        )}
        {config.showStepper && <ProfileCreationNavigation />}
        <h1 className={clsx('h3', styles.pageTitle)}>{config.title}</h1>
        <p>{t('surrogate.pageDescription')}</p>
      </GradientFormTitle>
      <SurrogateForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        personalInfo={patient}
        config={config}
        isLoading={isPending}
        surrogateVerified={!!patient.surrogate?.verified}
      />
    </PageContainer>
  );
}
